import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HomeIcon from '@mui/icons-material/Home'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Link as RouterLink } from 'react-router-dom'
import type { Manifest, Reference } from '../../api/authoring'
import { collectionTitle } from '../../model/records'
import { routes } from '../../model/routes'
import { flattenRoutes, routeMenus, type ResolvedRoute } from '../../model/website'
import { DragHandle } from '../../ui/DragHandle'
import { ComponentName } from '../../ui/ComponentName'
import { PinChip } from '../../ui/PinChip'
import { RowMenu } from '../../ui/RowMenu'
import { moved, useReorder, type Reorder } from '../../ui/reorder'
import { GeneratedRouteReport } from './GeneratedRouteReport'

/**
 * The routing tree, editable.
 *
 * Depth is drawn as indentation rather than as nesting, because the tree
 * is a list top to bottom and a linear rendering is the one view that
 * cannot misrepresent what is stored.
 *
 * Every action is labelled from the intent it sends, the way the
 * placement controls are — the markers say a payload names one route,
 * never whether naming it removes the route or makes it the home page.
 * What changed is where those words are: they were tooltips on six icon
 * buttons, and are now a menu that says them at rest.
 *
 * **A route is dragged to move it**, which is what the tool being
 * replaced does and what `website.routes_reordered` was always shaped
 * for: the intent carries a level's whole order, so a drop is one
 * command and one revision. A drag is confined to its own level, because
 * an order names the routes at one level and moving a route under a
 * different parent is an add and a remove rather than a rearrangement.
 */
export interface RouteActions {
  onAddUnder: (route: ResolvedRoute | undefined) => void
  onAddGeneratedUnder: (route: ResolvedRoute | undefined) => void
  onEdit: (route: ResolvedRoute) => void
  onRemove: (route: ResolvedRoute) => void
  onSetHome: (route: ResolvedRoute) => void
  /**
   * One level, rearranged.
   *
   * `under` is the page link id of the route this level sits beneath,
   * empty at the top — the same member `website.page_added` fills. The
   * order is the whole level, which is what the intent takes: one that
   * omits a route keeps it, so a short order can never be used to
   * remove.
   */
  onReorder: (under: string, order: string[]) => void
}

export interface RouteLabels {
  add: string
  addGenerated: string
  edit: string
  remove: string
  home: string
  move: string
}

export function RouteTree({
  routes,
  tenant,
  site,
  manifest,
  references,
  labels,
  actions,
  busy,
}: {
  routes: ResolvedRoute[]
  tenant: string
  site: string
  /** Read by a generated route's report, for its record type's key and fields. */
  manifest: Manifest | undefined
  /** The Website's own edges, where a generated route's source and pattern are. */
  references: Reference[]
  labels: RouteLabels
  actions: RouteActions
  busy: boolean
}) {
  const flat = flattenRoutes(routes)
  // One hook for the whole tree, with the level as the group. A hook per
  // level would be a hook count that changes with the routing, which
  // React forbids — and the levels are drawn interleaved in one flat
  // list anyway.
  const reorder = useReorder(actions.onReorder, !busy)

  return (
    <Paper>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="h3">Routes</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => actions.onAddUnder(undefined)}>
          {labels.add}
        </Button>
        <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => actions.onAddGeneratedUnder(undefined)}>
          {labels.addGenerated}
        </Button>
      </Stack>

      {flat.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
          No routes yet. Nothing on this site is reachable.
        </Typography>
      ) : (
        flat.map((route, index) => (
          <Box key={`${route.urlPath}-${route.key || index}`}>
            {index > 0 && <Divider />}
            <RouteRow
              route={route}
              tenant={tenant}
              site={site}
              manifest={manifest}
              references={references}
              labels={labels}
              actions={actions}
              reorder={reorder}
              busy={busy}
            />
          </Box>
        ))
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.25 }}>
        Drag a page to reorder it among the pages at its own level.
      </Typography>
    </Paper>
  )
}

function RouteRow({
  route,
  tenant,
  site,
  manifest,
  references,
  labels,
  actions,
  reorder,
  busy,
}: {
  route: ResolvedRoute
  tenant: string
  site: string
  manifest: Manifest | undefined
  references: Reference[]
  labels: RouteLabels
  actions: RouteActions
  reorder: Reorder<undefined>
  busy: boolean
}) {
  const grouping = !route.route.page && !route.generated
  const page = route.key
  const position = page ? route.siblings.indexOf(page) : -1
  const movable = !grouping && route.siblings.length > 1

  // A grouping route serves no page, so it has no key in the order and
  // cannot be dragged — the same reason it cannot be a parent or be
  // removed from here, and it is the model's doing rather than this
  // control's.
  const drag = movable ? reorder.rowProps(route.under, page, route.siblings) : {}
  const landing = movable ? reorder.landingOn(route.under, page) : undefined

  const step = (direction: -1 | 1) => {
    const target = route.siblings[position + direction]
    if (target === undefined) return
    const order = moved(route.siblings, page, target)
    if (order) actions.onReorder(route.under, order)
  }

  return (
    <Stack
      {...drag}
      direction="row"
      spacing={1.5}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{
        p: 1.5,
        pl: 1.5 + route.depth * 3,
        opacity: movable && reorder.isHeld(route.under, page) ? 0.4 : 1,
        borderTop: landing === 'above' ? 2 : 0,
        borderBottom: landing === 'below' ? 2 : 0,
        borderColor: 'primary.main',
      }}
    >
      {!grouping && <DragHandle label={labels.move} disabled={!movable || busy} />}

      {route.isHome && (
        <Tooltip title='This placement answers "/".'>
          <HomeIcon fontSize="small" color="primary" />
        </Tooltip>
      )}

      {/* A route opens the Page it reaches, addressed by that component's
          own id — the route is where the page is *placed*, the page is
          what an editor loads. Only a linked target can be opened: an
          inline page has no id and no stream of its own. */}
      {route.generated ? (
        <GeneratedLabel tenant={tenant} manifest={manifest} references={references} route={route} />
      ) : route.targetId ? (
        <MuiLink
          component={RouterLink}
          to={routes.pageStructure(tenant, site, route.targetId)}
          sx={{ fontWeight: 500 }}
        >
          {route.route.title || route.route.slug || '(no title)'}
        </MuiLink>
      ) : (
        <Typography sx={{ fontWeight: 500 }}>{route.route.title || route.route.slug || '(no title)'}</Typography>
      )}

      {!route.generated && <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>{route.urlPath}</Typography>}

      <Box sx={{ flexGrow: 1 }} />

      {routeMenus(route.route).length === 0 ? (
        <Tooltip title="In no menu. It is still reachable at its path.">
          <Chip size="small" variant="outlined" icon={<VisibilityOffIcon />} label="unlisted" />
        </Tooltip>
      ) : (
        <Tooltip title={`In ${routeMenus(route.route).join(', ')}.`}>
          <Stack direction="row" spacing={0.5}>
            {routeMenus(route.route).map((menu) => (
              <Chip key={menu} size="small" variant="outlined" label={menu} />
            ))}
          </Stack>
        </Tooltip>
      )}

      {route.generated ? (
        <>
          <GeneratedRouteReport tenant={tenant} manifest={manifest} references={references} route={route.route} />
          <Chip size="small" variant="outlined" label="generated" />
        </>
      ) : grouping ? (
        <Tooltip title="A grouping node: it appears in navigation without being reachable itself. It serves no page, and a route is named by the page it serves — so this one cannot be reordered, cannot be given children, and cannot be removed from here.">
          <Chip size="small" variant="outlined" icon={<WarningAmberIcon />} label="group" />
        </Tooltip>
      ) : route.dangling ? (
        <Tooltip title={`The route names link id "${route.route.page}", and this document has no such child.`}>
          <Chip size="small" color="error" icon={<WarningAmberIcon />} label="dangling" />
        </Tooltip>
      ) : (
        <>
          <Tooltip title="The placement's link id. A placement is addressed by this, never by the component id — the same component linked twice is two placements.">
            <Chip size="small" label={route.route.page} />
          </Tooltip>
          <PinChip pin={route.pin} />
        </>
      )}

      {!grouping && (
        <RowMenu
          disabled={busy}
          actions={[
            {
              label: 'Move up',
              icon: <ArrowUpwardIcon fontSize="small" />,
              disabled: position <= 0,
              detail: labels.move,
              onSelect: () => step(-1),
            },
            {
              label: 'Move down',
              icon: <ArrowDownwardIcon fontSize="small" />,
              disabled: position < 0 || position >= route.siblings.length - 1,
              detail: labels.move,
              onSelect: () => step(1),
            },
            {
              label: labels.home,
              icon: <HomeIcon fontSize="small" />,
              disabled: route.isHome || route.generated,
              detail: 'The page that answers "/".',
              onSelect: () => actions.onSetHome(route),
            },
            {
              label: labels.add,
              icon: <AddIcon fontSize="small" />,
              disabled: route.generated,
              detail: 'Under this one, as a child route.',
              onSelect: () => actions.onAddUnder(route),
            },
            {
              label: labels.addGenerated,
              icon: <AddIcon fontSize="small" />,
              // An empty slug adds no path segment, so records below it
              // would claim its siblings' paths. The Website refuses it.
              disabled: route.generated || !route.route.slug,
              detail: route.route.slug
                ? 'One page for every record in a collection.'
                : 'Not below a route with an empty slug: its records would take its siblings\' paths.',
              onSelect: () => actions.onAddGeneratedUnder(route),
            },
            {
              label: labels.edit,
              icon: <EditOutlinedIcon fontSize="small" />,
              detail: 'Its slug, its title, and whether it shows in navigation.',
              onSelect: () => actions.onEdit(route),
            },
            {
              label: labels.remove,
              icon: <DeleteOutlineIcon fontSize="small" />,
              destructive: true,
              detail: 'Drops the route and unlinks the page. The page itself is untouched.',
              onSelect: () => actions.onRemove(route),
            },
          ]}
        />
      )}
    </Stack>
  )
}

/**
 * A generated route's row, in the names an agency gave things: which
 * Collection's records get pages, where, and drawn by which pattern. The
 * edges it holds them by are the Website's business, not the reader's.
 */
function GeneratedLabel({
  tenant,
  manifest,
  references,
  route,
}: {
  tenant: string
  manifest: Manifest | undefined
  references: Reference[]
  route: ResolvedRoute
}) {
  const edge = (id: string | undefined, name: string) =>
    references.find((reference) => reference.edgeId === id && reference.name === name)
  const source = edge(route.route.source, 'routeSource')
  const pattern = edge(route.route.pattern, 'routePattern')
  // A Collection holds no name, so the one a record type lists is called
  // by that type's plural rather than by its id.
  const listedName = source && collectionTitle(manifest, source.id) !== source.id ? collectionTitle(manifest, source.id) : undefined
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>A page for each record in</Typography>
        {source ? (
          listedName ? (
            <Typography color="text.secondary">{listedName}</Typography>
          ) : (
            <ComponentName tenant={tenant} id={source.id} revision={source.revision} manifest={manifest} />
          )
        ) : (
          <Typography color="error">a Collection this site no longer holds</Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
          {route.urlPath}
        </Typography>
        {pattern && <ComponentName tenant={tenant} id={pattern.id} revision={pattern.revision} manifest={manifest} prefix="· " />}
      </Stack>
    </Stack>
  )
}
